/**
 * Opening sequence — single owner of the fresh-load chain.
 * Desktop: welcome → fading → chrome → characters → done
 * Flat: welcome → fading → chrome → done (no characters)
 */

import { IS_DESKTOP_EXPERIENCE } from './deviceGate'
import { MOTION } from './motion'
import {
  getSceneLoadState,
  subscribeSceneLoadTracker,
} from './sceneLoadTracker'
import {
  begin as beginTransition,
  end as endTransition,
  TRANSITION_KINDS,
} from './transitionCoordinator'

const OPENING_STEPS = Object.freeze([
  'welcome',
  'fading',
  'chrome',
  'characters',
  'done',
])

const STEP_INDEX = Object.fromEntries(
  OPENING_STEPS.map((step, index) => [step, index])
)

const readPrefersReducedMotion = () =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

let step = 'welcome'
let started = false
let phase1Unsub = null
let desktopWelcomeFallback = 0
let charactersFallbackTimer = 0
let flatUnsub = null
const listeners = new Set()

const stampHtml = () => {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-opening', step)
}

const emit = () => {
  listeners.forEach((listener) => listener(step))
}

const clearCharactersFallback = () => {
  if (!charactersFallbackTimer) return
  window.clearTimeout(charactersFallbackTimer)
  charactersFallbackTimer = 0
}

const setStep = (next) => {
  if (step === next) return
  step = next
  stampHtml()
  emit()
  if (next === 'characters') {
    clearCharactersFallback()
    charactersFallbackTimer = window.setTimeout(() => {
      charactersFallbackTimer = 0
      advance('characters')
    }, MOTION.ms.nav + 500)
  } else {
    clearCharactersFallback()
  }
  if (next === 'done') {
    endTransition(TRANSITION_KINDS.intro)
  }
}

stampHtml()

export const getOpeningStep = () => step

export const subscribeOpening = (listener) => {
  listeners.add(listener)
  listener(step)
  return () => listeners.delete(listener)
}

export const whenOpeningStep = (target) => {
  const targetIndex = STEP_INDEX[target]
  if (targetIndex == null) return Promise.resolve()

  return new Promise((resolve) => {
    if (STEP_INDEX[step] >= targetIndex) {
      resolve()
      return
    }
    const unsubscribe = subscribeOpening((current) => {
      if (STEP_INDEX[current] < targetIndex) return
      unsubscribe()
      resolve()
    })
  })
}

/** Advances only if current step === fromStep (late fallbacks cannot double-advance). */
export const advance = (fromStep) => {
  if (step !== fromStep) return false
  const index = STEP_INDEX[fromStep]
  if (index == null || index >= OPENING_STEPS.length - 1) return false
  if (!IS_DESKTOP_EXPERIENCE && fromStep === 'chrome') {
    setStep('done')
    return true
  }
  setStep(OPENING_STEPS[index + 1])
  return true
}

const tryAdvanceWelcomeFromPhase1 = () => {
  if (step !== 'welcome') return
  if (getSceneLoadState().timestamps.phase1Done == null) return
  window.clearTimeout(desktopWelcomeFallback)
  desktopWelcomeFallback = 0
  advance('welcome')
}

const DESKTOP_WELCOME_FALLBACK_MS = 120000

const startFlatOpening = () => {
  beginTransition(TRANSITION_KINDS.intro)
  setStep('welcome')

  const reduced = readPrefersReducedMotion()
  const fadeFallbackMs = MOTION.ms.fade + 150
  const chromeFallbackMs = MOTION.ms.intro + 150
  const welcomeFallbackMs = 8000

  let welcomeFallback = 0
  let fadingFallback = 0
  let chromeFallback = 0
  let fadingWatched = false
  let chromeWatched = false

  const finishWelcome = () => {
    window.clearTimeout(welcomeFallback)
    welcomeFallback = 0
    advance('welcome')
  }

  welcomeFallback = window.setTimeout(finishWelcome, welcomeFallbackMs)
  Promise.resolve(document.fonts?.ready)
    .catch(() => {})
    .then(() => new Promise((resolve) => {
      window.setTimeout(resolve, 600)
    }))
    .then(finishWelcome)

  const watchFading = () => {
    if (fadingWatched) return
    fadingWatched = true

    if (reduced) {
      advance('fading')
      return
    }

    const node =
      document.querySelector('.welcome-screen')
      || document.getElementById('welcome-static')
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      window.clearTimeout(fadingFallback)
      fadingFallback = 0
      node?.removeEventListener('transitionend', onTransitionEnd)
      advance('fading')
    }
    const onTransitionEnd = (event) => {
      if (event.target !== node) return
      if (event.propertyName && event.propertyName !== 'opacity') return
      finish()
    }
    node?.addEventListener('transitionend', onTransitionEnd)
    fadingFallback = window.setTimeout(finish, fadeFallbackMs)
  }

  const watchChrome = () => {
    if (chromeWatched) return
    chromeWatched = true

    if (reduced) {
      advance('chrome')
      return
    }

    const header = document.querySelector('.flat-header')
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      window.clearTimeout(chromeFallback)
      chromeFallback = 0
      header?.removeEventListener('animationend', onAnimationEnd)
      advance('chrome')
    }
    const onAnimationEnd = (event) => {
      if (event.target !== header) return
      if (event.animationName && event.animationName !== 'flat-header-drop') return
      finish()
    }
    header?.addEventListener('animationend', onAnimationEnd)
    chromeFallback = window.setTimeout(finish, chromeFallbackMs)
  }

  flatUnsub = subscribeOpening((current) => {
    if (current === 'fading') watchFading()
    if (current === 'chrome') watchChrome()
    if (current === 'done') {
      window.clearTimeout(welcomeFallback)
      window.clearTimeout(fadingFallback)
      window.clearTimeout(chromeFallback)
      flatUnsub?.()
      flatUnsub = null
    }
  })
}

export const startOpeningSequence = () => {
  if (started) return
  started = true

  if (!IS_DESKTOP_EXPERIENCE) {
    startFlatOpening()
    return
  }

  beginTransition(TRANSITION_KINDS.intro)
  setStep('welcome')

  desktopWelcomeFallback = window.setTimeout(() => {
    desktopWelcomeFallback = 0
    if (step === 'welcome') advance('welcome')
  }, DESKTOP_WELCOME_FALLBACK_MS)

  tryAdvanceWelcomeFromPhase1()
  phase1Unsub = subscribeSceneLoadTracker(() => {
    tryAdvanceWelcomeFromPhase1()
  })

  whenOpeningStep('done').then(() => {
    phase1Unsub?.()
    phase1Unsub = null
    window.clearTimeout(desktopWelcomeFallback)
    desktopWelcomeFallback = 0
  })
}

export const prefersReducedOpeningMotion = readPrefersReducedMotion
